import { createSlice } from "@reduxjs/toolkit";
import DeviceIssueTip from "components/Home/Modals/DeviceIssueTip/DeviceIssueTip";
import UrlPermissions from "graphics/Help/UrlPermissions";
import {
  startMeasuringMicLevel,
  stopMeasuringMicLevel,
} from "helpers/media/soundmeter";
import { setGeneralModal } from "./generalSlice";
import {
  setAudioInputDevice,
  setAudioOutputDevice,
  setCameraInputDevice,
} from "./mediaServerSlice";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

const initialState = {
  currentMic: null,
  currentMicTrackId: null,
  currentCamera: null,
  currentCameraTrackId: null,
  currentSpeakers: null,
  permissionsModal: false,
};

export var currentMicTrackObj = null;
export var currentCameraTrackObj = null;
window.getCurrentMicTrackObj = () => currentMicTrackObj;
window.getCurrentCameraTrackObj = () => currentCameraTrackObj;

export function setCurrentMicTrackObj(track) {
  if (currentMicTrackObj && !currentMicTrackObj._managedByDaily)
    currentMicTrackObj.stop();
  currentMicTrackObj = track;
}
export function setCurrentCameraTrackObj(track) {
  if (currentCameraTrackObj && !currentCameraTrackObj._managedByDaily)
    currentCameraTrackObj.stop();
  currentCameraTrackObj = track;
}

export const slice = createSlice({
  name: "devices",
  initialState,
  reducers: {
    setCurrentMic: (state, action) => {
      state.currentMic = action.payload;
    },
    setCurrentMicTrackId: (state, action) => {
      state.currentMicTrackId = action.payload;
    },
    setCurrentCamera: (state, action) => {
      state.currentCamera = action.payload;
    },
    setCurrentCameraTrackId: (state, action) => {
      state.currentCameraTrackId = action.payload;
    },
    setCurrentSpeakers: (state, action) => {
      state.currentSpeakers = action.payload;
    },
    setPermissionsModal: (state, action) => {
      state.permissionsModal = action.payload;
    },
    resetDevicesSlice: () => initialState,
  },
});

export const {
  setCurrentMic,
  setCurrentMicTrackId,
  setCurrentCamera,
  setCurrentCameraTrackId,
  setCurrentSpeakers,
  setPermissionsModal,
  resetDevicesSlice,
} = slice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state) => state.counter.value)`
export const selectCurrentMic = (state) => state.devices.currentMic;
export const selectCurrentMicTrackId = (state) =>
  state.devices.currentMicTrackId;
export const selectCurrentCamera = (state) => state.devices.currentCamera;
export const selectCurrentCameraTrackId = (state) =>
  state.devices.currentCameraTrackId;
export const selectCurrentSpeakers = (state) => state.devices.currentSpeakers;
export const selectPermissionsModal = (state) => state.devices.permissionsModal;

export default slice.reducer;

export const checkIfMediaAllowed = () => {
  return checkIfMicAllowed && checkIfCamAllowed;
};

const MIC_ALLOWED = "mic_allowed";
const CAM_ALLOWED = "cam_allowed";

const checkIfMicAllowed = () => {
  window.efs_debug &&
    window.log("CheckifMicAllowed: " + localStorage.getItem(MIC_ALLOWED));
  return localStorage.getItem(MIC_ALLOWED) !== null;
};

const checkIfCamAllowed = () => {
  window.efs_debug &&
    window.log("CheckifCamAllowed: " + localStorage.getItem(CAM_ALLOWED));

  return localStorage.getItem(CAM_ALLOWED) !== null;
};

// Determine whether we should show the advice to allow media permissions
export const requestPermissionsModal = async ({ mic = false, cam = false }) => {
  window.efs_debug &&
    window.log("RequestPermissionsModal Mic: " + mic + ". And Cam: " + cam);

  let askForIt = false;
  try {
    // Proper way that only chrome does
    const micPerm = await navigator.permissions.query({
      name: "microphone",
    });
    const camPerm = await navigator.permissions.query({
      name: "camera",
    });
    if (mic && micPerm.state !== "granted") askForIt = true;
    if (cam && camPerm.state !== "granted") askForIt = true;
  } catch (e) {
    // For safari & firefox
    window.efs_debug &&
      window.log(
        "Could not get actual permissions for requestPermissionsModal"
      );

    // Last resort, in case mic was allowed previously and cam was not allowed (or vice versa)
    // if (!askForIt) {
    //   if (mic && !checkIfMicAllowed()) askForIt = true;
    //   if (cam && !checkIfCamAllowed()) askForIt = true;
    // }

    // Will be undefined if there was not a previous successful call
    // console.log(await navigator.mediaDevices.enumerateDevices());
    // console.log((await navigator.mediaDevices.enumerateDevices())[0]);
    if (!(await navigator.mediaDevices.enumerateDevices())[0]?.label)
      askForIt = true;
  }
  window.efs_debug && window.log("AskForIt is: " + askForIt);

  if (askForIt) dispatch(setPermissionsModal(true));
  return askForIt;
};

export const setMicAllowed = () => {
  localStorage.setItem(MIC_ALLOWED, "true");
};

export const setCamAllowed = () => {
  localStorage.setItem(CAM_ALLOWED, "true");
};

export function setCurrentMicTrack(track) {
  setCurrentMicTrackObj(track);
  dispatch(setCurrentMicTrackId(track.id));
}

export function setCurrentCameraTrack(track) {
  setCurrentCameraTrackObj(track);
  dispatch(setCurrentCameraTrackId(track.id));
}

export function clearCurrentMicTrack() {
  window.efs_debug && window.log("Clearing Current Mic Track");

  if (currentMicTrackObj) setCurrentMicTrackObj(null);
  if (selectCurrentMicTrackId(getState())) dispatch(setCurrentMicTrackId(null));
}

export function clearCurrentCameraTrack() {
  window.efs_debug && window.log("Clearing Current Cam Track");

  if (currentCameraTrackObj) setCurrentCameraTrackObj(null);
  if (selectCurrentCameraTrackId(getState()))
    dispatch(setCurrentCameraTrackId(null));
}

export function startMeteringMyMicLevel() {
  window.efs_debug && window.log("Start Metering My Mic Level");

  if (currentMicTrackObj) {
    startMeasuringMicLevel(
      "meter",
      new MediaStream([currentMicTrackObj]),
      adjustMyMicMeter
    );
  }
}

export const stopMeteringMyMicLevel = async () => {
  window.efs_debug && window.log("Stop Metering My Mic Level");

  stopMeasuringMicLevel("meter");
  adjustMyMicMeter(0);
};

function adjustMyMicMeter(value) {
  let elem = document.getElementById("my-mic-white");
  if (elem) {
    if (value > 0.06) elem.style.width = "0%";
    else elem.style.width = (1 - value / 0.06) * 100 + "%";
  }
}

export async function enumerateAllDevices() {
  let devices = {
    audioinputs: [],
    videoinputs: [],
    audiooutputs: [],
  };
  // debugger;
  let askForIt = await requestPermissionsModal({
    mic: true,
    cam: true,
  });
  // Need to get devices to get permission
  try {
    let stream = null;
    if (askForIt) {
      window.efs_debug &&
        window.log("Will request streams just to gain permission");
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true,
      });
      setMicAllowed();
      setCamAllowed();
    }

    let deviceInfos = await navigator.mediaDevices.enumerateDevices();

    // For some reason i need to do this after so that enumerateDevices gives me labels on firefox
    if (stream) severStream(stream);

    window.efs_debug && window.log("Device Infos Enumerate All: ");
    window.efs_debug && window.log(JSON.stringify(deviceInfos));

    for (let i = 0; i < deviceInfos.length; i++) {
      let option = {};
      option.deviceId = deviceInfos[i].deviceId;
      if (deviceInfos[i].kind === "audioinput") {
        if (!selectCurrentMic(getState()))
          await setAudioInputDevice(option.deviceId);

        option.name =
          deviceInfos[i].label ||
          "Microphone " + (devices.audioinputs.length + 1);
        devices.audioinputs.push(option);
      } else if (deviceInfos[i].kind === "videoinput") {
        if (!selectCurrentCamera(getState()))
          await setCameraInputDevice(option.deviceId);

        option.name =
          deviceInfos[i].label || "Camera " + (devices.videoinputs.length + 1);
        devices.videoinputs.push(option);
      } else if (deviceInfos[i].kind === "audiooutput") {
        if (!selectCurrentSpeakers(getState()))
          setAudioOutputDevice(option.deviceId);

        option.name =
          deviceInfos[i].label ||
          "Speaker " + (devices.audiooutputs.length + 1);
        devices.audiooutputs.push(option);
      }
    }
  } catch (e) {
    window.efs_debug && window.log("NO DEVICES FOUND");
    deviceFailureHandler(e);
  }

  dispatch(setPermissionsModal(false));
  return devices;
}

export async function enumerateAudioInputs() {
  window.efs_debug && window.log("Will try Enumerate Audio Ins");

  let devices = {
    audioinputs: [],
  };

  let askForIt = requestPermissionsModal({ mic: true });
  try {
    let stream = null;
    if (askForIt) {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      setMicAllowed();
    }

    let deviceInfos = await navigator.mediaDevices.enumerateDevices();

    if (stream) severStream(stream);

    window.efs_debug && window.log("Device Infos Enumerate Audio Ins: ");
    window.efs_debug && window.log(JSON.stringify(deviceInfos), 2);
    for (let i = 0; i < deviceInfos.length; i++) {
      let option = {};
      option.deviceId = deviceInfos[i].deviceId;
      if (deviceInfos[i].kind === "audioinput") {
        if (!selectCurrentMic(getState()))
          await setAudioInputDevice(option.deviceId);
        option.name =
          deviceInfos[i].label ||
          "Microphone " + (devices.audioinputs.length + 1);
        devices.audioinputs.push(option);
      }
    }
  } catch (e) {
    deviceFailureHandler(e);
  }

  dispatch(setPermissionsModal(false));
  return devices;
}

export async function enumerateVideoInputs() {
  let devices = {
    videoinputs: [],
  };
  // debugger;
  let askForIt = requestPermissionsModal({ cam: true });
  try {
    let stream = null;
    if (askForIt) {
      stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      setCamAllowed();
    }

    let deviceInfos = await navigator.mediaDevices.enumerateDevices();

    if (stream) severStream(stream);

    window.efs_debug && window.log("Device Infos Enumerate Video Inputs: ");
    window.efs_debug && window.log(JSON.stringify(deviceInfos), 2);

    for (let i = 0; i < deviceInfos.length; i++) {
      let option = {};
      option.deviceId = deviceInfos[i].deviceId;
      if (deviceInfos[i].kind === "videoinput") {
        if (!selectCurrentCamera(getState()))
          await setCameraInputDevice(option.deviceId);

        option.name =
          deviceInfos[i].label || "Camera " + (devices.videoinputs.length + 1);
        devices.videoinputs.push(option);
      }
    }
  } catch (e) {
    deviceFailureHandler(e);
  }

  dispatch(setPermissionsModal(false));
  return devices;
}

export const startMic = async () => {
  window.efs_debug && window.log("Starting Camera by startCamera");

  if (selectCurrentMicTrackId(getState())) return;

  requestPermissionsModal({ mic: true });
  try {
    let stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: selectCurrentMic(getState()),
      },
    });
    setMicAllowed();
    let audioTrack = stream.getTracks()[0];
    window.efs_debug && window.log(`Using audio device: ${audioTrack.label}`);

    if (!selectCurrentMic(getState()))
      await setAudioInputDevice(audioTrack.getSettings().deviceId);

    setCurrentMicTrack(audioTrack);
  } catch (e) {
    window.efs_debug && window.log("startMic failed: " + e);
    deviceFailureHandler(e);
    clearCurrentMicTrack();
  }
  dispatch(setPermissionsModal(false));
};

export const startCamera = async (givenConstraints = true) => {
  window.efs_debug && window.log("Starting Camera by startCamera");

  if (selectCurrentCameraTrackId(getState())) return;

  requestPermissionsModal({ cam: true });
  try {
    let stream = await navigator.mediaDevices.getUserMedia({
      video: {
        ...givenConstraints,
        deviceId: selectCurrentCamera(getState()),
      },
    });
    setCamAllowed();
    let videoTrack = stream.getTracks()[0];
    window.efs_debug && window.log(stream);
    window.efs_debug && window.log(videoTrack);
    window.efs_debug && window.log(`Using video device: ${videoTrack.label}`);

    if (!selectCurrentCamera(getState()))
      await setCameraInputDevice(videoTrack.getSettings().deviceId);

    setCurrentCameraTrack(videoTrack);
  } catch (e) {
    window.efs_debug && window.log("startCamera failed: " + e);
    deviceFailureHandler(e);
    clearCurrentCameraTrack();
  }
  dispatch(setPermissionsModal(false));
};

export const severStream = (stream) => {
  try {
    stream.getTracks().forEach((track) => {
      track.stop();
      // stream.removeTrack(track);
    });
  } catch (e) {
    window.log("Could not sever stream. Error: " + JSON.stringify(e));
  }
};

export const deviceFailureHandler = (e) => {
  if (e.name === "NotAllowedError") {
    dispatch(
      setGeneralModal({
        title: "Your camera or microphone was blocked!",
        content: (
          <>
            Refresh the page after checking the icon in your url bar: <br />
            <UrlPermissions />
            <br />
            <br />
            (For Mac) ensure your browser is allowed access to your camera and
            microphone in the privacy page of System Preferences.
            <br />
            <br />
            <DeviceIssueTip />
          </>
        ),
      })
    );
  } else {
    dispatch(
      setGeneralModal({
        title: "Camera or microphone not found!",
        content: (
          <>
            Check your operating system permissions or try again later on a
            computer that has a working camera or microphone. <br />
            <br />
            <DeviceIssueTip />
          </>
        ),
      })
    );
  }
  // dispatch(setDeviceSettingsModal(false));
};
